import {inject, Injectable, signal, WritableSignal} from '@angular/core';
import {Observable} from 'rxjs';
import {AuctionModel} from '../models/auction.model';
import {environment} from '../../environments/environment';
import {HttpClient, HttpContext} from '@angular/common/http';
import {UserCode} from '../models/user-codes.model';
import {REQUIRE_AUTH} from '../interceptors/auth.interceptor';

@Injectable({providedIn: 'root'})
export class AuctionService {
  public auctionProducts: WritableSignal<AuctionModel> = signal({} as AuctionModel);
  public secondProductReservations: WritableSignal<number> = signal(0);
  public firstProductReservations: WritableSignal<number> = signal(0);

  private http: HttpClient = inject(HttpClient);
  private currentEventSource?: EventSource;

  auctionStream(auctionId: string): Observable<AuctionModel> {
    return new Observable(observer => {
      const url = `${environment.socketUrl}.well-known/mercure?topic=auction_${auctionId}`;
      const eventSource = new EventSource(url);

      this.currentEventSource = eventSource;

      eventSource.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          if (data?.id) {
            observer.next(data as AuctionModel);
          }
        } catch (error) {
          console.error('Błąd przy parsowaniu danych Mercure:', error);
        }
      };

      eventSource.onerror = error => {
        console.error('Błąd połączenia Mercure:', error);
        try { observer.complete(); } catch {}
        try { eventSource.close(); } catch {}
        if (this.currentEventSource === eventSource) {
          this.currentEventSource = undefined;
        }
      };

      return () => {
        try { eventSource.close(); } catch {}
        if (this.currentEventSource === eventSource) {
          this.currentEventSource = undefined;
        }
      };
    });
  }

  public reserveProduct(auctionId: string, itemType: string, levelNumber: number): Observable<{ reservationsCount: number }> {
    return this.http.post<{ reservationsCount: number }>(`${environment.apiUrl}reservations`,
      {
        auctionId,
        itemType,
        levelNumber
      }, {
        withCredentials: true
      });
  }

  public importCode(codeId: string): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}code/import`, {id: codeId, status: 'started'}, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
      withCredentials: true,
      headers: {
        'Content-Type': 'application/merge-patch+json',
        'Accept': 'application/json'
      }});
  }


  public useCode(codeId: string): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}code/use`, {id: codeId, status: 'used'}, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
      withCredentials: true,
      headers: {
        'Content-Type': 'application/merge-patch+json',
        'Accept': 'application/json'
      }});
  }

  public publishCode(codeId: string): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}code/publish`, {id: codeId}, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
      withCredentials: true,
      headers: {
        'Content-Type': 'application/merge-patch+json',
        'Accept': 'application/json'
      }});
  }

  public withdrawCode(codeId: string): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}code/withdraw`, {id: codeId}, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
      withCredentials: true,
      headers: {
        'Content-Type': 'application/merge-patch+json',
        'Accept': 'application/json'
      }});
  }

  public changeCodePrice(codeId: string, price: number): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}code/price`, {id: codeId, price}, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
      withCredentials: true,
      headers: {
        'Content-Type': 'application/merge-patch+json',
        'Accept': 'application/json'
      }});
  }

  public sendUserEmail(email: string, auctionId: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}auctions/summary/${auctionId}`,
      {
        email,
      },
      {
        withCredentials: true
      });
  }

  public getAuctionInfo(auctionId: string): Observable<{ reservations: number }> {
    return this.http.get<{ reservations: number }>(`${environment.apiUrl}auctions/ping/${auctionId}`, {
      withCredentials: true
    });
  }

  public getUserCodes(): Observable<UserCode[]> {
    return this.http.get<UserCode[]>(`${environment.apiUrl}codes`, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
      withCredentials: true
    });
  }

  public getAuctionDetails(auctionId: string): Observable<AuctionModel> {
    return this.http.get<AuctionModel>(`${environment.apiUrl}auctions/${auctionId}`,
      {
        withCredentials: true
      });
  }

  public stopStream(): void {
    try { this.currentEventSource?.close(); } catch {}
    this.currentEventSource = undefined;
  }
}
